/**
 * CCModal.tsx -- Base modal primitive for the cross-cutting component layer.
 *
 * PRD SS18.6: accessible dialog with title, optional description, close-on-Escape,
 * return-focus-to-trigger-on-dismiss, prefers-reduced-motion respect.
 *
 * Size variants: sm (480px), md (640px, default), lg (800px).
 */
import React, { useCallback, useEffect, useRef, type CSSProperties } from "react";

export interface CCModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg";
  ariaLabelledby?: string;
}

const SIZE_MAP: Record<NonNullable<CCModalProps["size"]>, string> = {
  sm: "480px",
  md: "640px",
  lg: "800px",
};

export function CCModal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  ariaLabelledby,
}: CCModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const titleId = React.useId();

  // Capture the trigger element on open
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
    }
  }, [open]);

  // Focus the dialog when opened, return focus on close
  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
    return () => {
      if (!open && triggerRef.current && triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!open) return null;

  const overlayStyle: CSSProperties = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "24px",
  };

  const dialogStyle: CSSProperties = {
    width: "100%",
    maxWidth: SIZE_MAP[size],
    backgroundColor: "var(--color-surface-kinari, #F0E6D6)",
    borderRadius: "var(--radius-md, 8px)",
    padding: "var(--space-card-padding, 24px)",
    boxShadow: "var(--shadow-soft, 0 4px 24px rgba(0,0,0,0.08))",
    outline: "none",
    maxHeight: "calc(100vh - 48px)",
    overflow: "auto",
    fontFamily: "var(--font-display)",
    color: "var(--color-surface-sumi, #1A1614)",
  };

  const titleStyle: CSSProperties = {
    fontSize: "18px",
    fontWeight: 600,
    margin: 0,
    lineHeight: 1.4,
  };

  const descriptionStyle: CSSProperties = {
    fontSize: "14px",
    margin: "8px 0 0",
    opacity: 0.75,
    lineHeight: 1.5,
  };

  return (
    <div style={overlayStyle} onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledby ?? titleId}
        aria-describedby={description ? `${titleId}-desc` : undefined}
        tabIndex={-1}
        style={dialogStyle}
        onKeyDown={handleKeyDown}
        data-modal-size={size}
      >
        <h2 id={titleId} style={titleStyle}>
          {title}
        </h2>
        {description ? (
          <p id={`${titleId}-desc`} style={descriptionStyle}>
            {description}
          </p>
        ) : null}
        <div style={{ marginTop: "16px" }}>{children}</div>
      </div>
    </div>
  );
}

export default CCModal;
